import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { BaseSidebarComponent } from './shared/components/az-base-sidebar/base-sidebar.component';
import { MatIconRegistry } from '@angular/material/icon';
import { filter } from 'rxjs';
import { ToolbarComponent } from './core/layout/components/toolbar/toolbar.component';
import { FooterComponent } from './core/layout/components/footer/footer.component';
import { ProfileService } from './core/auth/services/profile.service';
import { NotificationService } from './shared/services/notification.service';
import { AuthService } from './core/auth/services/auth.service';
import {
  DisclaimerAcceptanceService,
  ResolvedDisclaimer,
} from './shared/services/disclaimer-acceptance.service';
import { MatDialog } from '@angular/material/dialog';
import { LicenseAgreementDialogComponent } from './shared/components/license-agreement-dialog/license-agreement-dialog.component';
import { ToastComponent } from './shared/components/toast/toast.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, BaseSidebarComponent, ToolbarComponent, FooterComponent, ToastComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'AZ Translate - Quote Management';
  showSidebar = true;
  disclaimerInteractionLocked = false;
  private hasValidatedDisclaimer = false;
  private disclaimerCheckInProgress = false;
  private disclaimerDialogOpen = false;
  
  // Sidebar configuration
  readonly sidebarConfig = {
    logoUrl: '/assets/translate-logo-not-collapsed.svg',
    logoCompactUrl: '/assets/translate-logo-collapsed.svg',
    companyName: 'AstraZeneca',
    tagline: 'AZ Translate',
    taglineShort: 'AZ'
  };
  private router = inject(Router);
  private profileService = inject(ProfileService);
  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);
  private disclaimerAcceptanceService = inject(DisclaimerAcceptanceService);
  private dialog = inject(MatDialog);

  constructor() {
    // Configure Material Icons to use 'material-symbols-outlined' font
    const iconRegistry = inject(MatIconRegistry);
    iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
    
    // Track route changes to hide sidebar on auth pages
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.showSidebar = !event.urlAfterRedirects.startsWith('/auth');
        this.updateDisclaimerInteractionLock();
        this.ensureDisclaimerAcceptance();
      });
  }

  ngOnInit(): void {
    // Delay to allow auth session to be established
    setTimeout(() => this.loadProfile(), 1000);
    this.notificationService.startPolling();
  }

  private loadProfile(): void {
    this.profileService.getProfile().subscribe({
      next: (profile) => {
        console.log('✅ Profile loaded');
        this.ensureDisclaimerAcceptance();
        //if (profile.ngsMarket) {
        //  this.profileService.registerNgsProfile();
        //}
      },
      error: (error) => {
        console.error('❌ Profile load failed:', error.status, error.message);
      },
    });
  }

  private ensureDisclaimerAcceptance(): void {
    this.updateDisclaimerInteractionLock();

    if (this.hasValidatedDisclaimer || this.disclaimerCheckInProgress || this.disclaimerDialogOpen) {
      return;
    }

    if (this.router.url.startsWith('/auth')) {
      this.updateDisclaimerInteractionLock();
      return;
    }

    const auth = this.authService.getAuthFromLocalStorage();
    if (!auth) {
      this.updateDisclaimerInteractionLock();
      return;
    }

    this.disclaimerInteractionLocked = true;
    this.disclaimerCheckInProgress = true;
    this.disclaimerAcceptanceService.getResolvedDisclaimer().subscribe({
      next: (disclaimer) => {
        this.disclaimerCheckInProgress = false;

        if (!disclaimer || !this.disclaimerAcceptanceService.shouldRequireAcceptance(disclaimer)) {
          this.hasValidatedDisclaimer = true;
          this.updateDisclaimerInteractionLock();
          return;
        }

        this.openDisclaimerDialog(disclaimer);
      },
      error: (error) => {
        this.disclaimerCheckInProgress = false;
        this.updateDisclaimerInteractionLock();
        console.error('❌ Disclaimer load failed:', error);
      },
    });
  }

  private openDisclaimerDialog(disclaimer: ResolvedDisclaimer): void {
    this.disclaimerDialogOpen = true;
    const heading = `${disclaimer.title} - ${disclaimer.subHeading}`;

    const dialogRef = this.dialog.open(LicenseAgreementDialogComponent, {
      disableClose: true,
      autoFocus: false,
      restoreFocus: false,
      width: '1013px',
      maxWidth: '96vw',
      height: '688px',
      maxHeight: '96vh',
      panelClass: 'ula-dialog-panel',
      backdropClass: 'ula-dialog-backdrop',
      data: {
        disclaimerLang: disclaimer.disclaimerLang,
        disclaimerHtml: disclaimer.disclaimerInfo,
        heading,
        buttonText: disclaimer.buttonText,
        scrollHintText: disclaimer.alertMessage,
        saveErrorText:
          'We were unable to process your agreement at this time. Please refresh the page and try again. If the issue persists, contact support.',
      },
    });

    dialogRef.afterClosed().subscribe((accepted) => {
      this.disclaimerDialogOpen = false;
      if (accepted) {
        this.hasValidatedDisclaimer = true;
      }
      this.updateDisclaimerInteractionLock();
    });
  }

  private updateDisclaimerInteractionLock(): void {
    const isAuthRoute = this.router.url.startsWith('/auth');
    const hasAuth = !!this.authService.getAuthFromLocalStorage();
    this.disclaimerInteractionLocked =
      !isAuthRoute && hasAuth && !this.hasValidatedDisclaimer;
  }
}
